import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  PageHeader,
  PhotoTile,
  Skeleton,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import type { components } from "@/generated/openapi-types";
import { useApi } from "@/lib/api";
import { ago, count, dateTime, duration } from "@/lib/format";

type JobName = components["schemas"]["AdminJobName"];

/**
 * The machinery.
 *
 * Four things worth watching, and only two of them show up anywhere else:
 *
 *   - The photo pipeline, where `stuck` is the number that matters. A photo
 *     left pending for an hour means the S3 event never fired or the
 *     processor died mid-run, and neither of those reports itself.
 *   - The rollups, where "scores behind reviews" says whether the reconcile
 *     job is keeping up. Zero means yes; a number that grows daily means no.
 *   - The OSM imports, which run on somebody's LAPTOP and never in AWS. That
 *     is deliberate — see docs/OSM_INGEST.md — and it means nothing in
 *     CloudWatch will ever tell you the import stopped happening. This table
 *     is the only trace, which is why it is on this screen.
 *   - The jobs, two of which can be started from here.
 */
export function OpsScreen() {
  const api = useApi();
  const queryClient = useQueryClient();

  const ops = useQuery({
    queryKey: ["admin", "ops"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/ops");
      if (error) throw error;
      return data;
    },
    refetchInterval: 30_000,
  });

  const runJob = useMutation({
    mutationFn: async (job: JobName) => {
      const { error } = await api.POST("/admin/ops/jobs/{job}/run", {
        params: { path: { job } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "ops"] });
    },
  });

  if (ops.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader eyebrow="System" title="Ops" />
        <ErrorState onRetry={() => void ops.refetch()} />
      </div>
    );
  }

  const d = ops.data;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="System"
        title="Ops"
        description={
          d
            ? `${d.stage} · version ${d.version} · database ${
                d.database.ok ? "reachable" : "unreachable"
              } in ${d.database.latencyMs}ms`
            : undefined
        }
        actions={
          d ? (
            <Badge tone={d.database.ok ? "success" : "danger"}>
              {d.database.ok ? "database ok" : "database down"}
            </Badge>
          ) : null
        }
      />

      {!d ? (
        <Skeleton className="h-96" />
      ) : (
        <>
          <section className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader
                title="Photo pipeline"
                subtitle="Stuck means pending or processing for over an hour — that one does not report itself."
              />
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                <Figure label="Ready" value={d.photos.ready} />
                <Figure label="Pending" value={d.photos.pending} />
                <Figure label="Processing" value={d.photos.processing} />
                <Figure
                  label="Failed"
                  value={d.photos.failed}
                  tone={d.photos.failed > 0 ? "danger" : undefined}
                />
                <Figure
                  label="Stuck"
                  value={d.photos.stuck}
                  tone={d.photos.stuck > 0 ? "danger" : undefined}
                />
              </div>
              {d.photos.recentFailures.length > 0 ? (
                <div className="mt-4 border-t border-border-2 pt-3">
                  <p className="eyebrow mb-2">Recent failures</p>
                  <div className="flex flex-wrap gap-2">
                    {d.photos.recentFailures.map((photo) => (
                      <PhotoTile key={photo.id} photo={photo} />
                    ))}
                  </div>
                </div>
              ) : null}
            </Card>

            <Card>
              <CardHeader
                title="Rollups"
                subtitle="Whether the read models still agree with the claims underneath them."
              />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Figure label="Fact states" value={d.rollups.factStates} />
                <Figure
                  label="Stale facts"
                  value={d.rollups.staleFactStates}
                  tone={d.rollups.staleFactStates > 0 ? "warn" : undefined}
                />
                <Figure label="Scored cafes" value={d.rollups.scoredCafes} />
                <Figure
                  label="Scores behind"
                  value={d.rollups.scoresBehindReviews}
                  tone={d.rollups.scoresBehindReviews > 0 ? "warn" : undefined}
                  hint="a review newer than its score"
                />
                <Figure
                  label="Facts computed"
                  value={ago(d.rollups.lastFactComputedAt)}
                />
                <Figure
                  label="Scores computed"
                  value={ago(d.rollups.lastScoreComputedAt)}
                />
              </div>
              <p className="mt-3 border-t border-border-2 pt-3 text-micro text-muted">
                {count(d.accountsAwaitingPurge)} account
                {d.accountsAwaitingPurge === 1 ? "" : "s"} inside the seven-day
                grace window, waiting on the purge job.
              </p>
            </Card>
          </section>

          <Card>
            <CardHeader
              title="Jobs"
              subtitle="Started jobs run detached — this screen's numbers are how you watch them finish."
            />
            <ul className="flex flex-col divide-y divide-border-2">
              {d.jobs.map((job) => (
                <li
                  key={job.job}
                  className="flex flex-wrap items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <div className="min-w-[180px]">
                    <p className="text-[12.5px] font-medium text-ink">
                      {job.label}
                    </p>
                    <p className="text-micro text-muted">{job.schedule}</p>
                  </div>
                  <p className="min-w-0 flex-1 text-micro text-muted">
                    {job.description}
                  </p>
                  {job.runnable ? (
                    <Button
                      variant="primary"
                      disabled={runJob.isPending}
                      onClick={() => runJob.mutate(job.job)}
                    >
                      Run now
                    </Button>
                  ) : (
                    <Badge tone="neutral">not from here</Badge>
                  )}
                </li>
              ))}
            </ul>
          </Card>

          <Card padded={false}>
            <div className="px-4 pt-4">
              <CardHeader
                title="Imports"
                subtitle={
                  d.lastOsmIngestAt
                    ? `Last OSM import ${ago(d.lastOsmIngestAt)}. These run locally, so nothing else will tell you when they stop.`
                    : "No import has ever run against this database."
                }
              />
            </div>
            {d.ingestBatches.length === 0 ? (
              <p className="px-4 pb-6 text-micro text-muted">
                Nothing recorded.
              </p>
            ) : (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Kind</TH>
                      <TH>City</TH>
                      <TH>Started</TH>
                      <TH align="right">Took</TH>
                      <TH>Result</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {d.ingestBatches.map((batch) => (
                      <TR key={batch.id}>
                        <TD mono>{batch.kind}</TD>
                        <TD muted>{batch.city ?? "—"}</TD>
                        <TD muted mono>
                          {dateTime(batch.startedAt)}
                        </TD>
                        <TD align="right" mono>
                          {batch.finishedAt ? (
                            duration(batch.durationSeconds)
                          ) : (
                            <Badge tone="warn">still running</Badge>
                          )}
                        </TD>
                        <TD muted mono className="max-w-[320px]">
                          <span className="block truncate">
                            {batch.stats
                              ? Object.entries(batch.stats)
                                  .map(([k, v]) => `${k}=${String(v)}`)
                                  .join(" ")
                              : "—"}
                          </span>
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: "danger" | "warn";
}) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <p
        className={
          tone === "danger"
            ? "mt-0.5 font-mono text-[17px] leading-none text-danger"
            : tone === "warn"
              ? "mt-0.5 font-mono text-[17px] leading-none text-warn"
              : "mt-0.5 font-mono text-[17px] leading-none text-ink"
        }
      >
        {typeof value === "number" ? count(value) : value}
      </p>
      {hint ? <p className="mt-1 text-micro text-muted">{hint}</p> : null}
    </div>
  );
}
